import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { IconButton, Tooltip } from '@mui/material';
import {
  findSimilarMealIDInMenu,
  myFavouritesRecipesBoard,
  myFavouritesRecipesBoardDefaultMenu,
} from '../../services/smorg_board';
import {
  useCreateMealMutation,
  useDeleteMealMutation,
  useGetDistilledMealsByParentQuery,
  useGetFullMealQuery,
  useGetFullSharedMealQuery,
  useGetSmorgBoardQuery,
  useUpdateSmorgBoardOptimisticallyMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetDistilledMealByIDAndParentQuery,
  useGetDistilledSharedMealByIDAndParentQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { recipeCollectionsCardDOMID } from '../../services/meals';
import {
  copyMealToBoard,
  copySharedMealToBoard,
} from '../../operations/recipes_operations';
import { unfavouriteMeal } from '../../operations/meal_operations';
import { useTracker } from '../../operations/tracking_operations';

const MealFavouriteButton = ({ mealID, parentID, afterAddingToFavourites }) => {
  const dispatch = useDispatch();

  const { ref, inView } = useInView({
    triggerOnce: true,
    fallbackInView: true,
  });

  const { data: userProfile } = useGetUserProfileQuery();

  const tracker = useTracker();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const { data: distilledMealInMeals } = useGetDistilledMealByIDAndParentQuery({
    id: mealID,
    parentID,
  });

  const { data: distilledMealInSharedMeals } =
    useGetDistilledSharedMealByIDAndParentQuery({ id: mealID, parentID });

  const distilledMeal = distilledMealInMeals || distilledMealInSharedMeals;

  // Normally I would select the favourites recipes board from the
  // ownedRecipesBoards, but this query does not update after manually patching
  // in response to the updateSmorgBoard mutation.
  // I don't know if it's something I did wrong, or if it's a bug in the
  // RTK Query library.
  //
  // const favouritesRecipesBoard = myFavouritesRecipesBoard(ownedRecipesBoards);
  // const favouritesMenu = myFavouritesRecipesBoardDefaultMenu(
  //   favouritesRecipesBoard ? [favouritesRecipesBoard] : [],
  // );

  const favouritesRecipesBoardID =
    myFavouritesRecipesBoard(ownedRecipesBoards)?.id;

  const { data: favouritesRecipesBoard } = useGetSmorgBoardQuery(
    favouritesRecipesBoardID,
    { skip: !favouritesRecipesBoardID },
  );

  const favouritesMenu = myFavouritesRecipesBoardDefaultMenu(
    favouritesRecipesBoard ? [favouritesRecipesBoard] : [],
  );

  const { data: favouriteDistilledMeals } = useGetDistilledMealsByParentQuery(
    favouritesRecipesBoard?.id,
    {
      skip: !favouritesRecipesBoard?.id,
    },
  );

  const mealIDInFavourites = findSimilarMealIDInMenu(
    distilledMeal,
    favouritesMenu,
    favouriteDistilledMeals,
  );

  const isFavourite = !!mealIDInFavourites;

  const isSharedMeal = !!distilledMealInSharedMeals;

  const { data: fullMealInMeals } = useGetFullMealQuery(mealID, {
    skip: !inView || isSharedMeal,
  });

  const { data: fullMealInSharedMeals } = useGetFullSharedMealQuery(mealID, {
    skip: !inView || !isSharedMeal,
  });

  const fullMeal = fullMealInMeals || fullMealInSharedMeals;

  // const [lazyGetMeal] = useLazyGetFullMealQuery();

  // const [lazyGetSharedMeal] = useLazyGetFullSharedMealQuery();

  const [createMeal] = useCreateMealMutation();

  const [deleteMeal] = useDeleteMealMutation();

  const [updateSmorgBoard] = useUpdateSmorgBoardOptimisticallyMutation();

  console.log(
    `Favourite menu contains ${
      (favouritesMenu?.mealIDs || []).length
    } meals, last ID is ${favouritesMenu?.mealIDs?.slice(-1)[0]}`,
  );

  const onClickFavourite = useCallback(async () => {
    console.log(`Begin favouriting meal ${mealID}`);
    const cb = (copiedMeal) => {
      window.setTimeout(() => {
        const el = document.getElementById(
          recipeCollectionsCardDOMID(copiedMeal.id),
        );
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 200);
    };

    if (isSharedMeal) {
      // const { data: fullSharedMeal } = await lazyGetSharedMeal(mealID, true);
      const fullSharedMeal = fullMeal;
      await copySharedMealToBoard(
        fullSharedMeal,
        favouritesRecipesBoard,
        favouritesMenu?.id,
        currentSpaceMembership,
        updateSmorgBoard,
        createMeal,
        userProfile,
        tracker,
        null,
        cb,
        dispatch,
      );
    } else {
      // const { data: fullMeal } = await lazyGetMeal(mealID, true);
      await copyMealToBoard(
        fullMeal,
        favouritesRecipesBoard,
        favouritesMenu?.id,
        currentSpaceMembership,
        updateSmorgBoard,
        createMeal,
        userProfile,
        tracker,
        null,
        cb,
        dispatch,
        null,
      );
    }

    if (afterAddingToFavourites) {
      afterAddingToFavourites({
        favouritesRecipesBoardID: favouritesRecipesBoard.id,
      });
    }
    console.log(`End favouriting meal ${mealID}`);
  }, [
    mealID,
    isSharedMeal,
    afterAddingToFavourites,
    fullMeal,
    favouritesRecipesBoard,
    favouritesMenu?.id,
    currentSpaceMembership,
    updateSmorgBoard,
    createMeal,
    userProfile,
    tracker,
    dispatch,
  ]);

  const onClickUnfavourite = useCallback(
    () =>
      unfavouriteMeal(
        mealIDInFavourites,
        favouritesRecipesBoard,
        favouritesMenu,
        deleteMeal,
        updateSmorgBoard,
        dispatch,
      ),
    [
      deleteMeal,
      dispatch,
      favouritesMenu,
      favouritesRecipesBoard,
      mealIDInFavourites,
      updateSmorgBoard,
    ],
  );

  if (parentID === favouritesRecipesBoard?.id) {
    return null;
  }

  if (!favouritesMenu) {
    return null;
  }

  return (
    <Tooltip title="Add meal to your favourites" placement="top-start">
      {isFavourite ? (
        <IconButton
          aria-label="favourite"
          size="small"
          onClick={(ev) => {
            onClickUnfavourite(ev.target);
            ev.stopPropagation();
          }}>
          <FavoriteIcon />
        </IconButton>
      ) : (
        <IconButton
          ref={ref}
          aria-label="favourite"
          size="small"
          onClick={(ev) => {
            onClickFavourite(ev.target);
            ev.stopPropagation();
          }}>
          <FavoriteBorderIcon />
        </IconButton>
      )}
    </Tooltip>
  );
};

MealFavouriteButton.propTypes = {
  mealID: PropTypes.string.isRequired,
  parentID: PropTypes.string.isRequired,
  afterAddingToFavourites: PropTypes.func,
};

MealFavouriteButton.defaultProps = {
  afterAddingToFavourites: null,
};

export default MealFavouriteButton;
